/**
 * The three.js half of the funnel plume: one fixed-capacity mesh holding every
 * sloop's smoke, filled from `flight/ship-smoke.ts`'s arithmetic.
 *
 * `ShipWakeRibbons` next door, with two differences and no third:
 *
 * 1. **The colours are rewritten every frame**, where a wake's are baked once.
 *    A plume's walk down the ramp depends on how badly she is hurt, and that
 *    changes as she is shot. It is 126 vertices a ship — two buffers touched
 *    instead of one, on a pool that already costs nothing.
 * 2. **A slot is never declined.** A wake refuses to draw below a fifth of a
 *    knot; a ship stopped dead still has a fire in her, and one going down
 *    smokes hardest of all, so every ship pushed is a ship drawn.
 *
 * Everything else is the wake's: one indexed `Mesh` whose position attribute is
 * rewritten rather than an `InstancedMesh`, `begin`/`push`/`end`, `setDrawRange`
 * publishing only the slots that were filled, hidden outright when nothing was,
 * and **no transparency anywhere** — the fade is a walk down `BARON.PAL`'s own
 * grey run, which is both what a 256-colour game could draw and the way past
 * this renderer's standing trap that a `transparent` mesh under reversed-Z draws
 * zero pixels (`combat-visuals.ts`).
 */

import * as THREE from "three/webgpu";
import { SHIP_MAX_COUNT } from "@red-opus/engine";
import { SHIP_WATERLINE_Y_M } from "./ship-mounts.ts";
import {
  PLUME_RAMP,
  SMOKE_INDEX_COUNT,
  SMOKE_VERTEX_COUNT,
  smokeIndices,
  writeSmokeColours,
  writeSmokePositions,
  type SmokePoseAt,
} from "../flight/ship-smoke.ts";

/** A fixed-capacity pool of plumes: one mesh, one draw call for the whole fleet. */
export class ShipSmokeRibbons {
  readonly mesh: THREE.Mesh;
  private readonly positions: THREE.BufferAttribute;
  private readonly colours: THREE.BufferAttribute;
  private readonly vertices: Float32Array;
  private readonly tints: Float32Array;
  private written = 0;

  constructor(capacity: number = SHIP_MAX_COUNT) {
    const geometry = new THREE.BufferGeometry();
    this.vertices = new Float32Array(capacity * SMOKE_VERTEX_COUNT * 3);
    this.tints = new Float32Array(capacity * SMOKE_VERTEX_COUNT * 3);
    this.positions = new THREE.BufferAttribute(this.vertices, 3);
    this.colours = new THREE.BufferAttribute(this.tints, 3);
    this.positions.setUsage(THREE.DynamicDrawUsage);
    this.colours.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute("position", this.positions);
    geometry.setAttribute("color", this.colours);
    // Built once for the full capacity with a per-slot base offset, so a sortie
    // with one ship publishes a third of the indices and the untouched two
    // thirds of the buffers are never read.
    const template = smokeIndices();
    const indices = new Uint16Array(capacity * SMOKE_INDEX_COUNT);
    for (let slot = 0; slot < capacity; slot += 1) {
      const base = slot * SMOKE_VERTEX_COUNT;
      for (let at = 0; at < SMOKE_INDEX_COUNT; at += 1) {
        indices[slot * SMOKE_INDEX_COUNT + at] = (template[at] ?? 0) + base;
      }
    }
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    this.mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }),
    );
    // The pool's bounds are the fleet's, which the culler cannot know from an
    // empty buffer — the wake makes the same call for the same reason.
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  begin(): void {
    this.written = 0;
  }

  /**
   * Lays one ship's plume, from her past poses and how badly she is hurt.
   *
   * `false` means the pool was full and nothing was written; there is no other
   * way to be declined.
   */
  push(poseAt: SmokePoseAt, distress: number): boolean {
    if (this.written >= this.capacity) return false;
    const base = this.written * SMOKE_VERTEX_COUNT;
    writeSmokePositions(this.vertices, base, poseAt, SHIP_WATERLINE_Y_M, distress);
    writeSmokeColours(this.tints, base, PLUME_RAMP, distress);
    this.written += 1;
    return true;
  }

  /** Publishes this frame's writes. */
  end(): void {
    this.mesh.geometry.setDrawRange(0, this.written * SMOKE_INDEX_COUNT);
    this.mesh.visible = this.written > 0;
    this.positions.needsUpdate = true;
    this.colours.needsUpdate = true;
  }

  /** Ships whose plume the last `end()` published. */
  get liveCount(): number {
    return this.written;
  }

  get capacity(): number {
    return this.positions.count / SMOKE_VERTEX_COUNT;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    // Freeing the material is what releases this render object's bindings under
    // the WebGPU backend (2026-08-19 world-stability lesson), not bookkeeping.
    (this.mesh.material as THREE.Material).dispose();
  }
}
